import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// The Timesheets day pop-up's columns (Phelipe 2026-10-29): the day, where the
// phone was, and the map. Two lines between them drag to resize; the widths
// are kept per person in this browser, and a double-click puts them back.
const KEY = "timesheets.dayColumns"
const MIN_LEFT = 520
const MIN_MAP = 320
const MIN_WHERE = 180
const MAX_WHERE = 520

export default class extends Controller {
  static targets = ["body", "left", "handle"]

  connect() {
    this.apply(this.saved())
    this.observer = new ResizeObserver(() => this.place())
    if (this.hasBodyTarget) this.observer.observe(this.bodyTarget)
    this.place()
  }

  disconnect() {
    this.observer?.disconnect()
    this.stop()
  }

  saved() {
    try {
      return JSON.parse(window.localStorage.getItem(KEY) || "{}") || {}
    } catch (_e) {
      return {}
    }
  }

  save(widths) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(widths))
    } catch (_e) {
      // Private windows: the widths last until the pop-up closes.
    }
  }

  apply({ split, where } = {}) {
    if (split) this.element.style.setProperty("--day-split", `${split}px`)
    else this.element.style.removeProperty("--day-split")
    if (where) this.element.style.setProperty("--day-where", `${where}px`)
    else this.element.style.removeProperty("--day-where")
  }

  // The lines sit on the column edges: the left one where the places start,
  // the right one between the day and the map.
  place() {
    if (!this.hasBodyTarget || !this.hasLeftTarget) return
    const body = this.bodyTarget.getBoundingClientRect()
    const left = this.leftTarget.getBoundingClientRect()
    const row = this.leftTarget.querySelector("[data-timeline-step]")
    const whereCell = row?.lastElementChild?.getBoundingClientRect()
    this.handleTargets.forEach(handle => {
      handle.hidden = false
      if (handle.dataset.which === "split") handle.style.left = `${left.right - body.left}px`
      else if (whereCell) handle.style.left = `${whereCell.left - body.left}px`
      else handle.hidden = true
    })
  }

  start(event) {
    event.preventDefault()
    this.dragging = event.currentTarget.dataset.which
    this.moveHandler = e => this.move(e)
    this.upHandler = () => this.stop(true)
    window.addEventListener("pointermove", this.moveHandler)
    window.addEventListener("pointerup", this.upHandler)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }

  move(event) {
    const body = this.bodyTarget.getBoundingClientRect()
    const left = this.leftTarget.getBoundingClientRect()
    const widths = this.current()
    if (this.dragging === "split") {
      widths.split = Math.round(Math.min(Math.max(event.clientX - body.left, MIN_LEFT), body.width - MIN_MAP))
    } else {
      const padRight = parseFloat(getComputedStyle(this.leftTarget).paddingRight) || 0
      widths.where = Math.round(Math.min(Math.max(left.right - padRight - event.clientX, MIN_WHERE), MAX_WHERE))
    }
    this.widths = widths
    this.apply(widths)
    this.place()
  }

  stop(done = false) {
    if (!this.dragging) return
    window.removeEventListener("pointermove", this.moveHandler)
    window.removeEventListener("pointerup", this.upHandler)
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    if (done && this.widths) {
      this.save(this.widths)
      track("Timesheet Day Columns Resized", { column: this.dragging })
    }
    this.dragging = null
  }

  current() {
    const style = this.element.style
    const split = parseInt(style.getPropertyValue("--day-split"), 10)
    const where = parseInt(style.getPropertyValue("--day-where"), 10)
    return { split: Number.isFinite(split) ? split : null, where: Number.isFinite(where) ? where : null }
  }

  reset() {
    this.widths = null
    this.save({})
    this.apply({})
    this.place()
  }
}
